import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { NativeSelect } from './ui/native-select';
import Axios from '../Axios';
import toast from 'react-hot-toast';
import './SettingsPage.css';

const MAX_PROFILE_IMAGE_SIZE = 2 * 1024 * 1024;
export function SettingsPage() {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  const [avatarInitials, setAvatarInitials] = useState('JD');
  const [profileImage, setProfileImage] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [profileStatus, setProfileStatus] = useState('');
  const fileInputRef = useRef(null);
  const [imageFile, setImageFile] = useState(null);
  const [notifications, setNotifications] = useState({
    tradeAlerts: true,
    priceAlerts: false,
    promoEmails: true
  });
  const [profile, setProfile] = useState({

  })
  const [wallet, setWallet] = useState({
    balance: 0
  })
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [securityStatus, setSecurityStatus] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [isTwoFactorModalOpen, setIsTwoFactorModalOpen] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState('setup');
  const [twoFactorAction, setTwoFactorAction] = useState('enable');
  const [twoFactorMethod, setTwoFactorMethod] = useState('authenticator');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorSetupCode, setTwoFactorSetupCode] = useState('');
  const [twoFactorOtpAuthUrl, setTwoFactorOtpAuthUrl] = useState('');
  const [isTwoFactorSubmitting, setIsTwoFactorSubmitting] = useState(false);
  const [balance, setBalance] = useState(10250);
  const [walletAmount, setWalletAmount] = useState('100');
  const [walletStatus, setWalletStatus] = useState('');
  const [language, setLanguage] = useState('English');
  const [currency, setCurrency] = useState('USD');
  const [timezone, setTimezone] = useState('UTC');
  const [preferencesStatus, setPreferencesStatus] = useState('');
  const balanceText = useMemo(() => isNaN(wallet.balance) ? Number(0).toLocaleString(
    'en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }
  ) : Number(wallet.balance).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }), [wallet]);
  // const saveProfile = () => {
  //   const derivedInitials = displayName
  //     .split(' ')
  //     .map((part) => part.trim().charAt(0).toUpperCase())
  //     .join('')
  //     .slice(0, 2);
  //   if (derivedInitials) {
  //     setAvatarInitials(derivedInitials);
  //   }
  //   setProfileStatus(`Saved at ${new Date().toLocaleTimeString('en-US', {
  //     hour: '2-digit',
  //     minute: '2-digit'
  //   })}`);
  // };
  const handleProfileImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      setProfileStatus('Please upload a valid image file.');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_PROFILE_IMAGE_SIZE) {
      setProfileStatus('Image is too large. Max size is 2MB.');
      event.target.value = '';
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setProfileImage(reader.result);
        setProfileStatus('Profile image updated.');
      }
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const openTwoFactorModal = async (action = 'enable') => {
    const isDisableFlow = action === 'disable';
    setTwoFactorAction(action);
    setTwoFactorStep(isDisableFlow ? 'verify' : 'setup');
    setTwoFactorMethod('authenticator');
    setTwoFactorCode('');
    setTwoFactorSetupCode('');
    setTwoFactorOtpAuthUrl('');
    setIsTwoFactorModalOpen(true);
    if (!isDisableFlow) {
      await getTwoFaCode();
    }
  };

  const disableTwoFactor = async (secret) => {
    try {
      if (!/^\d{6}$/.test(secret)) {
        setSecurityStatus('Enter a valid 6-digit verification code.');
        return;
      }

      setIsTwoFactorSubmitting(true);
      const { data } = await Axios.post('/disable-twofa', { secret });
      if (data?.success) {
        setTwoFactorEnabled(false);
        setIsTwoFactorModalOpen(false);
        setTwoFactorCode('');
        setTwoFactorSetupCode('');
        setTwoFactorOtpAuthUrl('');
        setSecurityStatus(data?.message || 'Two-factor authentication disabled.');
        toast.success(data?.message || 'Two-factor authentication disabled.');
      } else {
        toast.error(data?.message || 'Failed to disable 2FA');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Something went wrong');
    } finally {
      setIsTwoFactorSubmitting(false);
    }
  };

  const handleTwoFactorPrimaryAction = async () => {
    if (twoFactorEnabled) {
      await openTwoFactorModal('disable');
      return;
    }
    await openTwoFactorModal('enable');
  };
  const continueTwoFactorSetup = () => {
    setTwoFactorStep('verify');
  };
  const verifyTwoFactorCode = async () => {
    if (!/^\d{6}$/.test(twoFactorCode.trim())) {
      setSecurityStatus('Enter a valid 6-digit verification code.');
      return;
    }

    if (twoFactorAction === 'disable') {
      await disableTwoFactor(twoFactorCode.trim());
      return;
    }

    const email = profile?.email;
    if (!email) {
      toast.error('Email is required to verify 2FA');
      return;
    }

    try {
      setIsTwoFactorSubmitting(true);
      const { data } = await Axios.post('/verify-twofa', {
        email,
        status: 'verify',
        secret: twoFactorCode.trim()
      });

      if (data?.success) {
        setTwoFactorEnabled(true);
        setIsTwoFactorModalOpen(false);
        setTwoFactorCode('');
        setTwoFactorSetupCode('');
        setTwoFactorOtpAuthUrl('');
        setSecurityStatus(data?.message || 'Two-factor authentication enabled successfully.');
        toast.success(data?.message || '2FA verified successfully');
      } else {
        toast.error(data?.message || 'Invalid OTP');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Something went wrong');
    } finally {
      setIsTwoFactorSubmitting(false);
    }
  };
  // const updateWallet = (type) => {
  //   const parsedAmount = Number(walletAmount);
  //   if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
  //     setWalletStatus('Enter a valid amount.');
  //     return;
  //   }
  //   if (type === 'withdraw' && parsedAmount > balance) {
  //     setWalletStatus('Insufficient balance.');
  //     return;
  //   }
  //   setWallet((previous) => ({
  //     ...previous,
  //     balance: type === 'add' ? previous.balance + parsedAmount : previous.balance - parsedAmount
  //   }));
  //   setWalletStatus(type === 'add' ? `Added $${parsedAmount.toFixed(2)}` : `Withdrew $${parsedAmount.toFixed(2)}`);
  // };
  const savePreferences = () => {
    setPreferencesStatus('Preferences saved.');
  };
  const toggleNotification = (key) => {
    setNotifications((previous) => ({
      ...previous,
      [key]: !previous[key]
    }));
  };
  const uploadImage = async () => {
    try {
      const file = imageFile;
      const formData = new FormData();
      formData.append('image', file);
      const { data } = await Axios.post('/uploadimage', formData);
      if (data?.success) {
        return data?.result;
      } else {
        return null;
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Something went wrong');
      return null
    }
  }
  const getProfile = async () => {
    try {
      const { data } = await Axios.post('/users/profile');
      if (data?.success) {
        setProfile(data.result);
        const name = data?.result?.name || '';
        setDisplayName(name || '-');
        const initials = name
          .split(' ')
          .map((part) => part.trim().charAt(0).toUpperCase())
          .join('')
          .slice(0, 2) || 'JD';
        setAvatarInitials(initials);
        if (data?.result?.profile_image) {
          setProfileImage(data.result.profile_image);
        }
        setTwoFactorEnabled(data?.result?.f2A_Status === true || data?.result?.f2A_Status === 'true');
      } else {
        setProfile({});
        setDisplayName('-');
      }
    } catch (error) {
      setProfile({});
      setDisplayName('-');
    }
  }
  const getWallet = async () => {
    try {
      const { data } = await Axios.post('/users/getWallet', {},);
      if (data?.success) {
        setWallet(data?.result);
      } else {
        setWallet({ balance: 0 });
      }
    } catch (error) {
      setWallet({ balance: 0 });
      console.error('Error fetching wallet:', error);
    }
  }
  const changePassword = async () => {
    try {
      if (!currentPassword || currentPassword.trim() === '') {
        return toast.error('Current password is required');
      } else if (!newPassword || newPassword.trim() === '') {
        return toast.error('New password is required');
      } else if (!confirmPassword || confirmPassword.trim() === '') {
        return toast.error('Confirm password is required');
      } else if (newPassword.trim() === currentPassword.trim()) {
        return toast.error('New password must be different from current password');
      } else if (newPassword !== confirmPassword) {
        return toast.error('New password and confirm password do not match');
      } else {
        const payload = {
          oldPassword: currentPassword,
          newPassword: newPassword
        }
        const { data } = await Axios.post('/change-password', payload);
        if (data?.success) {
          toast.success(data?.message || 'Password changed successfully');
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        } else {
          toast.error(data?.message || 'Failed to change password');
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Cannot change password right now');
    }
  }
  const updateProfile = async () => {
    try {
      if (!displayName.trim()) {
        toast.error('Name is required');
        return;
      }
      if (!imageFile && displayName === profile?.name) {
        return toast.error('No changes to update');
      }
      let imageUrl = null;
      if (imageFile) {
        imageUrl = await uploadImage();
      }
      const { data } = await Axios.post('/users/updateprofile', {
        name: displayName,
        image: imageUrl
      });
      if (data?.success) {
        getProfile();
        toast.success(data?.message || 'Profile updated successfully');
      } else {
        toast.error(data?.message || 'Failed to update profile');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Something went wrong');
    }
  }

  const getTwoFaCode = async () => {
    try {
      const { data } = await Axios.post('/generate-twofa');
      if (data?.success) {
        const generatedCode = data?.result?.secret || data?.result?.code || data?.result?.base32 || '';
        const otpAuthUrl = data?.result?.otpauth_url || data?.result?.otpAuthUrl || '';
        setTwoFactorSetupCode(generatedCode);
        setTwoFactorOtpAuthUrl(otpAuthUrl);
        return generatedCode;
      } else {
        toast.error(data?.message || 'Failed to get 2FA code');
        setTwoFactorSetupCode('');
        setTwoFactorOtpAuthUrl('');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong');
      setTwoFactorSetupCode('');
      setTwoFactorOtpAuthUrl('');
    }
  }

  const copyTwoFactorUri = async () => {
    if (!twoFactorOtpAuthUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(twoFactorOtpAuthUrl);
      toast.success('2FA setup URI copied');
    } catch (_error) {
      toast.error('Unable to copy URI');
    }
  };

  useEffect(() => {
    let isMounted = true;
    Promise.allSettled([getProfile(), getWallet()]).finally(() => {
      if (isMounted) setIsLoaded(true);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section className={`settings-page-pro ${isLoaded ? 'is-page-ready' : 'page-is-loading'}`}>
      <header className="settings-header-pro">
        <h1>Settings</h1>
        <p>Manage your account preferences</p>
      </header>

      <Card as="section" className="settings-card-pro">
        <h2>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FC4420" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
          Profile Settings
        </h2>
        <div className="settings-profile-head-pro">
          <span className="settings-avatar-pro">
            {profileImage
              ? <img src={profileImage} alt="Profile" className="settings-avatar-image" />
              : avatarInitials}
          </span>
          <button type="button" className="settings-btn-pro settings-btn-pro--small" onClick={() => fileInputRef.current?.click()}>
            Change Avatar
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleProfileImageUpload}
          />
        </div>

        <div className="settings-fields-row">
          <label className="settings-field-pro">
            <span>Display Name</span>
            <Input value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Your Name" />
          </label>
          <label className="settings-field-pro">
            <span>Email Address</span>
            <Input value={profile?.email || ''} readOnly />
          </label>
        </div>

        <div className="settings-actions-pro">
          <button type="button" className="settings-btn-pro settings-btn-pro--primary" onClick={updateProfile}>
            Save Changes
          </button>
          <button type="button" className="settings-btn-pro" onClick={() => {
            setDisplayName(profile?.name || "-");
          }}>
            Cancel
          </button>
        </div>
        {profileStatus ? <p className="settings-status-pro">{profileStatus}</p> : null}
      </Card>

      <Card as="section" className="settings-card-pro">
        <h2>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FC4420" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>
          Notifications
        </h2>
        <div className="settings-toggle-list-pro">
          <article className="settings-toggle-row-pro">
            <div>
              <h3>Trade Alerts</h3>
              <p>Get notified when your trades complete</p>
            </div>
            <button type="button" className={`settings-switch-pro ${notifications.tradeAlerts ? 'is-on' : ''}`} onClick={() => toggleNotification('tradeAlerts')} aria-pressed={notifications.tradeAlerts} />
          </article>
          <article className="settings-toggle-row-pro">
            <div>
              <h3>Price Alerts</h3>
              <p>Receive alerts for significant price movements</p>
            </div>
            <button type="button" className={`settings-switch-pro ${notifications.priceAlerts ? 'is-on' : ''}`} onClick={() => toggleNotification('priceAlerts')} aria-pressed={notifications.priceAlerts} />
          </article>
          <article className="settings-toggle-row-pro">
            <div>
              <h3>Promotional Emails</h3>
              <p>Receive updates about new features</p>
            </div>
            <button type="button" className={`settings-switch-pro ${notifications.promoEmails ? 'is-on' : ''}`} onClick={() => toggleNotification('promoEmails')} aria-pressed={notifications.promoEmails} />
          </article>
        </div>
      </Card>

      <Card as="section" className="settings-card-pro">
        <h2>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FC4420" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
          Security
        </h2>
        <div className="settings-security-grid-pro">
          <label className="settings-field-pro">
            <span>Current Password</span>
            <Input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} placeholder="Enter Current Password" />
          </label>
          <label className="settings-field-pro">
            <span>New Password</span>
            <Input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="Enter New Password" />
          </label>
          <label className="settings-field-pro">
            <span>Confirm New Password</span>
            <Input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Confirm New Password" />
          </label>
        </div>

        <div className="settings-actions-pro">
          <button type="button" className="settings-btn-pro settings-btn-pro--primary" onClick={changePassword}>
            Update Password
          </button>
        </div>
        {securityStatus ? <p className="settings-status-pro">{securityStatus}</p> : null}

        <article className="settings-twofa-pro">
          <div>
            <h3>Two-Factor Authentication</h3>
            <p>Add an extra layer of security to prevent unauthorized access</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span className={`twofa-status-pill ${twoFactorEnabled ? 'is-active' : 'is-inactive'}`}>
              {twoFactorEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <button type="button" className="settings-btn-pro settings-btn-pro--small" onClick={handleTwoFactorPrimaryAction}>
              {twoFactorEnabled ? 'Disable' : 'Enable'}
            </button>
          </div>
        </article>
      </Card>

      <Card as="section" className="settings-card-pro">
        <h2>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FC4420" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"></path><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"></path><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path></svg>
          Wallet &amp; Payments
        </h2>
        <div className="settings-wallet-box-pro">
          <div className="wallet-balance-meta">
            <p>Current Balance</p>
            <strong>{balanceText}</strong>
          </div>
          <div className="settings-wallet-actions-pro">
            <button type="button" className="settings-btn-pro settings-btn-pro--primary" onClick={() => navigate('/deposit')}>
              Deposit
            </button>
            <button type="button" className="settings-btn-pro" onClick={() => navigate('/withdraw')}>
              Withdraw
            </button>
          </div>
          {walletStatus ? <p className="settings-status-pro" style={{ width: '100%' }}>{walletStatus}</p> : null}
        </div>
        <label className="settings-field-pro">
          <span>Withdrawal Address</span>
          <Input defaultValue="Enter wallet address" />
        </label>
      </Card>

      <Card as="section" className="settings-card-pro">
        <h2>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FC4420" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path><path d="M2 12h20"></path></svg>
          Preferences
        </h2>
        <div className="settings-preferences-grid-pro">
          <label className="settings-field-pro">
            <span>Language</span>
            <NativeSelect value={language} onChange={(event) => setLanguage(event.target.value)}>
              <option>English</option>
              <option>Spanish</option>
              <option>Hindi</option>
            </NativeSelect>
          </label>
          <label className="settings-field-pro">
            <span>Currency</span>
            <NativeSelect value={currency} onChange={(event) => setCurrency(event.target.value)}>
              <option>USD ($)</option>
              <option>EUR (€)</option>
              <option>USDT (₮)</option>
            </NativeSelect>
          </label>
          <label className="settings-field-pro">
            <span>Timezone</span>
            <NativeSelect value={timezone} onChange={(event) => setTimezone(event.target.value)}>
              <option>UTC</option>
              <option>America/New_York</option>
              <option>Asia/Dubai</option>
            </NativeSelect>
          </label>
        </div>
        <div className="settings-actions-pro">
          <button type="button" className="settings-btn-pro settings-btn-pro--primary" onClick={savePreferences}>
            Save Preferences
          </button>
        </div>
        {preferencesStatus ? <p className="settings-status-pro">{preferencesStatus}</p> : null}
      </Card>

      {isTwoFactorModalOpen ? (
        <div className="settings-modal-backdrop-pro" onClick={() => setIsTwoFactorModalOpen(false)}>
          <div className="settings-modal-pro" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <header>
              <h3>{twoFactorAction === 'disable' ? 'Disable Two-Factor Authentication' : 'Two-Factor Authentication'}</h3>
              <button
                type="button"
                className="settings-modal__close-pro"
                onClick={() => setIsTwoFactorModalOpen(false)}
              >
                ✕
              </button>
            </header>

            <p className="settings-modal__text-pro">
              {twoFactorAction === 'disable'
                ? 'Enter your 6-digit authenticator code to disable 2FA.'
                : 'Set up and verify 2FA to improve your account security.'}
            </p>

            {twoFactorStep === 'setup' && twoFactorAction !== 'disable' ? (
              <div className="settings-modal__setup-box-pro">
                <p>Use this setup code in your authenticator app.</p>
                <strong>{twoFactorSetupCode || 'Generating...'}</strong>
                {twoFactorOtpAuthUrl ? (
                  <div className="settings-modal__qr-wrap-pro">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(twoFactorOtpAuthUrl)}`}
                      alt="2FA QR code"
                      width="180"
                      height="180"
                      className="settings-modal__qr"
                    />
                  </div>
                ) : null}
                {twoFactorOtpAuthUrl ? (
                  <div style={{ marginTop: '0.65rem' }}>
                    <button type="button" className="settings-btn-pro settings-btn-pro--small" onClick={copyTwoFactorUri}>
                      Copy URI
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {twoFactorStep === 'verify' ? (
              <label className="settings-field-pro" style={{ marginTop: '0.7rem' }}>
                <span>Enter 6-digit code</span>
                <Input
                  value={twoFactorCode}
                  onChange={(event) => setTwoFactorCode(event.target.value)}
                  placeholder="123456"
                  inputMode="numeric"
                  maxLength={8}
                />
              </label>
            ) : null}

            <div className="settings-modal__actions-pro">
              <button
                type="button"
                className="settings-btn-pro"
                onClick={() => {
                  if (twoFactorStep === 'verify' && twoFactorAction !== 'disable') {
                    setTwoFactorStep('setup');
                    return;
                  }
                  setIsTwoFactorModalOpen(false);
                }}
                disabled={isTwoFactorSubmitting}
              >
                {twoFactorStep === 'verify' && twoFactorAction !== 'disable' ? 'Back' : 'Cancel'}
              </button>

              {twoFactorStep === 'setup' && twoFactorAction !== 'disable' ? (
                <button
                  type="button"
                  className="settings-btn-pro settings-btn-pro--primary"
                  onClick={continueTwoFactorSetup}
                  disabled={isTwoFactorSubmitting}
                >
                  Continue
                </button>
              ) : (
                <button
                  type="button"
                  className="settings-btn-pro settings-btn-pro--primary"
                  onClick={verifyTwoFactorCode}
                  disabled={isTwoFactorSubmitting}
                >
                  {isTwoFactorSubmitting
                    ? (twoFactorAction === 'disable' ? 'Disabling...' : 'Verifying...')
                    : (twoFactorAction === 'disable' ? 'Disable 2FA' : 'Verify')}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
